import {
  Icon,
  Checkbox,
  Tooltip,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  useFSTrackEvent,
} from '@/dc-shared-web/src'
import { cn } from '@/dc-shared-web/src/lib'
import { FC } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { step2Schema } from '@/app/(dashboard)/upload/step2/Step2Schema'
import { faCheck, faCircleInfo } from '@document-crunch/pro-regular-svg-icons'
import {
  SuggestedChecklist,
  SuggestedChecklistsByDocumentType,
} from '@/dc-shared-web/src/hooks/api/office/types'
import { AssignUser } from '@/app/(dashboard)/upload/step2/AssignUser'
import { useOfficeUserAssignments } from '@/dc-shared-web/src/hooks/api/office/useOfficeUserAssignments'
import { SPEC_DOC_TYPE_NAME } from '@/dc-shared-web/src/hooks/api/document/constants'
import { FS, FS_CATEGORY } from '@/dc-shared-web/src/lib/trackingConstants'

type DocumentTypeOptionsProps = {
  documentType: SuggestedChecklistsByDocumentType
  form: ReturnType<typeof useForm<z.infer<typeof step2Schema>>>
  officeUUID: string
  projectId?: string
  disabled: boolean
}

export const DocumentTypeOptions: FC<DocumentTypeOptionsProps> = ({
  documentType,
  form,
  officeUUID,
  projectId,
  disabled,
}) => {
  const { checklists, playbooks } = documentType.suggestedChecklists.reduce(
    (acc, item) => {
      if (item.type === 'checklist') {
        acc.checklists.push(item)
      } else if (item.type === 'playbook') {
        /* A crunch can only have one playbook so we overwrite the array. */
        acc.playbooks = [item]
      }

      return acc
    },
    {
      checklists: [] as SuggestedChecklist[],
      playbooks: [] as SuggestedChecklist[],
    }
  )

  const hasChecklists = checklists.length > 0
  const hasPlaybooks = playbooks.length > 0
  const showTabs = hasChecklists && hasPlaybooks
  const hasContent = documentType.suggestedChecklists.length > 0

  return (
    <div className='tw-text-sm tw-space-y-2'>
      {!hasContent && <EmptySuggestedOptions />}
      {hasContent && (
        <>
          {showTabs ? (
            <Tabs defaultValue='checklists' className='tw-w-full'>
              <TabsList fullWidth>
                <TabsTrigger
                  value='checklists'
                  data-testid='upload-tab-checklists'
                >
                  Checklists
                </TabsTrigger>
                <TabsTrigger value='playbook' data-testid='upload-tab-playbook'>
                  <span className='tw-inline-flex tw-items-center tw-gap-1.5'>
                    Playbook
                    <Tooltip
                      content={
                        <div className='tw-max-w-64'>
                          <p>
                            Playbooks break down contract terms into actionable
                            steps for your project team.
                          </p>
                        </div>
                      }
                      withArrow
                    >
                      <span className='tw-inline-flex tw-items-center'>
                        <Icon
                          icon={faCircleInfo}
                          size='xs'
                          className='tw-text-neutral-500'
                        />
                      </span>
                    </Tooltip>
                  </span>
                </TabsTrigger>
              </TabsList>
              <TabsContent value='checklists' className='tw-mt-4'>
                <div className='tw-mb-3'>
                  <p>
                    Select checklists to process with this crunch{' '}
                    <span className='tw-text-neutral-500'>(Recommended)</span>
                  </p>
                </div>
                <Options
                  options={checklists}
                  form={form}
                  officeUUID={officeUUID}
                  projectId={projectId}
                  disabled={disabled}
                />
              </TabsContent>
              <TabsContent value='playbook' className='tw-mt-4'>
                <div className='tw-mb-3'>
                  <p>
                    Select playbook to process with this crunch{' '}
                    <span className='tw-text-neutral-500'>(Optional)</span>
                  </p>
                </div>
                <Options
                  options={playbooks}
                  form={form}
                  officeUUID={officeUUID}
                  projectId={projectId}
                  disabled={disabled}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <>
              {hasChecklists && (
                <>
                  <div className='tw-mb-3'>
                    <p>
                      Select checklists to process with this crunch{' '}
                      <span className='tw-text-neutral-500'>(Recommended)</span>
                    </p>
                  </div>
                  <Options
                    options={checklists}
                    form={form}
                    officeUUID={officeUUID}
                    projectId={projectId}
                    disabled={disabled}
                  />
                </>
              )}
              {hasPlaybooks && (
                <>
                  <div className='tw-mb-3'>
                    <p>
                      Select playbook to process with this crunch{' '}
                      <span className='tw-text-neutral-500'>(Optional)</span>
                    </p>
                  </div>
                  <Options
                    options={playbooks}
                    form={form}
                    officeUUID={officeUUID}
                    projectId={projectId}
                    disabled={disabled}
                  />
                </>
              )}
            </>
          )}
          <div className='tw-pt-2'>
            <ChatOption documentTypeName={documentType.docTypeName} />
          </div>
        </>
      )}
    </div>
  )
}

type SelectableOptions = {
  options: SuggestedChecklist[]
  form: ReturnType<typeof useForm<z.infer<typeof step2Schema>>>
  officeUUID: string
  projectId?: string
  disabled: boolean
}

const Options: FC<SelectableOptions> = ({
  options,
  form,
  officeUUID,
  projectId,
  disabled,
}) => {
  const { trackEvent } = useFSTrackEvent()
  const { data: userAssignments, isLoading } = useOfficeUserAssignments({
    officeUUID,
    projectId,
  })

  const selectedChecklists = useWatch({
    control: form.control,
    name: 'checklists',
  })

  const selectedPlaybooks = useWatch({
    control: form.control,
    name: 'playbooks',
  })

  const assignments = useWatch({
    control: form.control,
    name: 'assignments',
  })

  const handleCheckboxChange = (option: SuggestedChecklist) => {
    if (option.type === 'checklist') {
      const newChecklists = selectedChecklists.includes(option.uuid)
        ? selectedChecklists.filter((id) => id !== option.uuid)
        : [...selectedChecklists, option.uuid]

      form.setValue('checklists', newChecklists)
    } else if (option.type === 'playbook') {
      const newPlaybooks = selectedPlaybooks.includes(option.uuid)
        ? selectedPlaybooks.filter((id) => id !== option.uuid)
        : [...selectedPlaybooks, option.uuid]

      form.setValue('playbooks', newPlaybooks)
    }

    form.setValue(
      'assignments',
      assignments.filter((assignment) => assignment.uuid !== option.uuid)
    )

    const itemIndex = options.findIndex((item) => item.uuid === option.uuid)
    const isNowSelected = !isChecked(option)

    trackEvent(FS.UPLOAD_CHECKLIST_SELECTED, {
      name: option.name,
      selected: isNowSelected,
      page_category: FS_CATEGORY.UPLOAD,
      item_length: options.length,
      item_order: itemIndex + 1,
    })
  }

  const isChecked = (option: SuggestedChecklist) => {
    if (option.type === 'checklist') {
      return selectedChecklists.includes(option.uuid)
    }

    if (option.type === 'playbook') {
      return selectedPlaybooks.includes(option.uuid)
    }

    return false
  }

  const addUser = (option: SuggestedChecklist, userUuid: string) => {
    let nextAssignments = [...assignments]

    /* If any, remove any assignment for the same option. */
    nextAssignments = nextAssignments.filter(
      (assignment) => assignment.uuid !== option.uuid
    )

    /* Add the new assignment. */
    nextAssignments.push({
      uuid: option.uuid,
      type: option.type,
      userUuid,
    })

    form.setValue('assignments', nextAssignments)
  }

  const removeUser = (option: SuggestedChecklist) => {
    form.setValue(
      'assignments',
      assignments.filter((assignment) => assignment.uuid !== option.uuid)
    )
  }

  const getSelectedUserUuid = (option: SuggestedChecklist) => {
    return assignments.find((assignment) => assignment.uuid === option.uuid)
      ?.userUuid
  }

  return (
    <div
      className='tw-rounded-lg tw-bg-gray-50 tw-p-4 tw-h-full tw-max-h-[264px] tw-space-y-2 tw-overflow-y-auto'
      data-testid='document-type-options'
    >
      {options.map((option) => (
        <div key={`${option.uuid}-${option.name}`}>
          <div className='tw-rounded-lg tw-bg-white tw-border tw-border-gray-200 tw-px-4 tw-py-2 tw-flex tw-items-center tw-gap-2 tw-h-16'>
            <Checkbox
              checked={isChecked(option)}
              onCheckedChange={() => handleCheckboxChange(option)}
              disabled={disabled}
              data-testid={`document-type-option-${option.uuid}-checkbox`}
            />
            <div
              className={cn(
                'tw-flex-1',
                !disabled && 'hover:tw-cursor-pointer'
              )}
              onClick={() => !disabled && handleCheckboxChange(option)}
              data-testid={`document-type-option-${option.uuid}-label`}
            >
              <Tooltip
                withArrow
                content={
                  <div className='tw-max-w-64'>{option.description}</div>
                }
                active={!!option.description}
              >
                <span>{option.name}</span>
              </Tooltip>
            </div>
            {isLoading ? (
              <UserAssignmentSkeleton />
            ) : (
              <AssignUser
                inputPlaceholder={PLACEHOLDER_BY_TYPE[option.type]}
                users={userAssignments ?? []}
                selectedUserUuid={getSelectedUserUuid(option)}
                addUser={(userUuid) => {
                  if (!isChecked(option)) {
                    handleCheckboxChange(option)
                  }

                  addUser(option, userUuid)
                }}
                removeUser={() => {
                  removeUser(option)
                }}
                onDropdownOpen={() => {
                  trackEvent(FS.DROPDOWN_CLICKED, {
                    name: 'UserAssignment',
                    text: 'Assign User',
                    page_category: FS_CATEGORY.UPLOAD,
                  })
                }}
                onUserAssigned={(userName) => {
                  trackEvent(FS.DROPDOWN_OPTION_SELECTED, {
                    name: userName,
                    parent: 'UserAssignment',
                    page_category: FS_CATEGORY.UPLOAD,
                    item_length: userAssignments?.length || 0,
                    item_order:
                      userAssignments!.findIndex(
                        (user) =>
                          `${user.firstname} ${user.lastname}` === userName
                      ) + 1,
                  })
                }}
                onUserUnassigned={() => {
                  trackEvent(FS.DROPDOWN_OPTION_SELECTED, {
                    name: 'Unassigned',
                    parent: 'UserAssignment',
                    page_category: FS_CATEGORY.UPLOAD,
                  })
                }}
                disabled={disabled}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const PLACEHOLDER_BY_TYPE = {
  checklist: 'Assign to checklist',
  playbook: 'Assign to playbook',
}

const EmptySuggestedOptions = () => {
  return (
    <div className='tw-rounded-lg tw-bg-gray-50 tw-px-4 tw-py-2 tw-flex tw-items-center tw-justify-center tw-h-[50px]'>
      <p className='tw-text-gray-500'>
        No checklists and/or a playbook available for this document type
      </p>
    </div>
  )
}

const ChatOption = ({ documentTypeName }: { documentTypeName: string }) => {
  const target = documentTypeName === SPEC_DOC_TYPE_NAME ? 'specs' : 'document'

  return (
    <div className='tw-flex tw-items-center tw-gap-2'>
      <Icon icon={faCheck} className='tw-text-success-600' />
      <p>Chatting with your {target} document is always included</p>
    </div>
  )
}

const UserAssignmentSkeleton = () => {
  return (
    <div className='tw-rounded-full tw-size-8 tw-animate-skeleton-shimmer' />
  )
}
